import React from "react";
import { Bolt, CheckCircle, ChevronRight, Error as ErrorIcon, Schedule, Sync } from "@mui/icons-material";
import { Box, Chip, CircularProgress, Tooltip, Typography } from "@mui/material";

import { NavigationButton } from "ui";

import { BASE_DATA_LAYERS, BaseDataLayerKey } from "./layers";
import { BaseDataRowState, BaseDataRowStatus } from "./rowState";

interface BaseDataRowProps {
  layerKey: BaseDataLayerKey;
  rowState: BaseDataRowState;
  /** Number of features in the layer, if known. */
  count: number | null | undefined;
  selected: boolean;
  disabled: boolean;
  onClick(): void;
}

const StatusIcon: React.FC<{ status: BaseDataRowStatus; caption: string }> = ({ status, caption }) => {
  switch (status) {
    case "done":
      return <CheckCircle sx={{ color: (theme) => theme.palette.success.main, fontSize: 18 }} />;
    case "partial":
      return (
        <Tooltip title={caption}>
          <ErrorIcon sx={{ color: (theme) => theme.palette.warning.main, fontSize: 18 }} />
        </Tooltip>
      );
    case "required":
      return (
        <Tooltip title={caption}>
          <ErrorIcon sx={{ color: (theme) => theme.palette.biarri.secondary.inactiveGrey, fontSize: 18 }} />
        </Tooltip>
      );
    case "ready":
      return (
        <Tooltip title={caption}>
          <Bolt sx={{ color: "common.white", bgcolor: "primary.light", borderRadius: "50%", fontSize: 18 }} />
        </Tooltip>
      );
    case "unavailable":
      return (
        <Tooltip title={`Unable to generate. ${caption}`}>
          <Bolt
            aria-label="Unable to generate"
            sx={{ color: "common.white", bgcolor: (theme) => theme.palette.biarri.secondary.inactiveGrey, borderRadius: "50%", fontSize: 18 }}
          />
        </Tooltip>
      );
    case "stale":
      return (
        <Tooltip title="Out of date. Generate again to update">
          <Sync sx={{ color: "common.white", bgcolor: "warning.main", borderRadius: "50%", fontSize: 18 }} />
        </Tooltip>
      );
    case "loading":
    case "running":
      return <CircularProgress size={16} color="warning" aria-label={caption} />;
    case "queued":
      return (
        <Tooltip title={caption}>
          <Schedule sx={{ color: (theme) => theme.palette.biarri.secondary.inactiveGrey, fontSize: 18 }} />
        </Tooltip>
      );
    default:
      return null;
  }
};

/** A base data layer row, showing the layer's status and opening its flyout when clicked. */
const BaseDataRow: React.FC<BaseDataRowProps> = ({ layerKey, rowState, count, selected, disabled, onClick }) => {
  const { name, icon: Icon, group } = BASE_DATA_LAYERS[layerKey];
  const { status, caption, isNext } = rowState;
  const showCount = status === "done" && group !== "designArea" && count !== null && count !== undefined;

  return (
    <NavigationButton
      icon={<Icon />}
      iconSize="small"
      title={name}
      checked={status === "done"}
      disabled={disabled}
      onClick={onClick}
      variant="outlined"
      sx={{
        my: 0,
        borderRadius: 0,
        "&:not(:first-of-type)": { mt: "-1px" },
        "&:first-of-type": { borderTopLeftRadius: 4, borderTopRightRadius: 4 },
        "&:last-of-type": { borderBottomLeftRadius: 4, borderBottomRightRadius: 4 },
        ...(selected && { backgroundColor: "rgba(33, 150, 243, 0.05)", borderColor: "primary.main", zIndex: 1 }),
      }}
      rightElement={
        <Box display="flex" alignItems="center" gap={1}>
          {isNext && <Chip size="small" color="primary" variant="outlined" label="NEXT" sx={{ fontWeight: 600, fontSize: 11 }} />}
          {showCount && (
            <Chip
              size="small"
              label={
                <Typography variant="body3" fontWeight={400}>
                  {count.toLocaleString()}
                </Typography>
              }
            />
          )}
          <StatusIcon status={status} caption={caption} />
          <ChevronRight sx={{ fontSize: 20, color: selected ? "primary.main" : "action.disabled" }} />
        </Box>
      }
    />
  );
};

export default BaseDataRow;
